import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, Calculator, ArrowRight, CalendarDays, Loader2} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface BusinessCalendar {
  id: string;
  orgId: string;
  name: string;
  isDefault: boolean;
  workingDays: number[];
  timezone: string;
}

interface CalendarException {
  id: string;
  calendarId: string;
  name: string;
  description: string | null;
  exceptionDate: string;
  exceptionType: "holiday" | "closure" | "working_day";
  isRecurring: boolean;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function BusinessCalendarPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [exceptionDialog, setExceptionDialog] = useState(false);
  const [excForm, setExcForm] = useState<{
    name: string; description: string; exceptionDate: string;
    exceptionType: string; isRecurring: boolean;
  }>({ name: "", description: "", exceptionDate: "", exceptionType: "holiday", isRecurring: false });

  const [calcStart, setCalcStart] = useState("");
  const [calcDays, setCalcDays] = useState("30");
  const [calcResult, setCalcResult] = useState<string | null>(null);

  const { data: calendars = [] } = useQuery<BusinessCalendar[]>({
    queryKey: ["/api/business-calendars"],
    queryFn: () => api.get<BusinessCalendar[]>(`/business-calendars`, sessionId),
  });
  const defaultCal = calendars.find((c) => c.isDefault) || calendars[0];

  const { data: exceptions = [] } = useQuery<CalendarException[]>({
    queryKey: ["/api/business-calendars", defaultCal?.id, "exceptions"],
    queryFn: () => api.get<CalendarException[]>(`/business-calendars/${defaultCal!.id}/exceptions`, sessionId),
    enabled: !!defaultCal,
  });

  const updateCalMutation = useMutation({
    mutationFn: (patch: Partial<BusinessCalendar>) =>
      api.patch(`/business-calendars/${defaultCal!.id}`, patch, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-calendars"] });
      toast({ title: "Calendar updated" });
    },
  });

  const addExceptionMutation = useMutation({
    mutationFn: () => api.post(`/business-calendars/${defaultCal!.id}/exceptions`, excForm, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-calendars", defaultCal?.id, "exceptions"] });
      setExceptionDialog(false);
      setExcForm({ name: "", description: "", exceptionDate: "", exceptionType: "holiday", isRecurring: false });
      toast({ title: "Exception added" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteExceptionMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/business-calendars/${defaultCal!.id}/exceptions/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-calendars", defaultCal?.id, "exceptions"] });
      toast({ title: "Exception removed" });
    },
  });

  const toggleWorkingDay = (day: number) => {
    if (!defaultCal) return;
    const current = defaultCal.workingDays || [1, 2, 3, 4, 5];
    if (current.includes(day) && current.length <= 1) {
      toast({ title: "At least one working day is required", variant: "destructive" });
      return;
    }
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort();
    updateCalMutation.mutate({ workingDays: next });
  };

  const handleCalculate = async () => {
    if (!calcStart || !calcDays || !defaultCal) {
      toast({ title: "Start date and working days are required", variant: "destructive" });
      return;
    }
    try {
      const params = new URLSearchParams({
        startDate: new Date(calcStart + "T12:00:00Z").toISOString(),
        workingDays: calcDays,
        calendarId: defaultCal.id,
      });
      const result = await api.get<{ resultDate: string }>(`/business-calendars/calculate?${params}`, sessionId);
      setCalcResult(new Date(result.resultDate).toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      }));
    } catch {
      toast({ title: "Calculation failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business Calendar"
        description="Configure working days, holidays, and closures for date calculations"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setExceptionDialog(true)} disabled={!defaultCal}>
              <Plus className="h-4 w-4 mr-1" /> Add Exception
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Working Days</CardTitle>
          <p className="text-sm text-muted-foreground">Select which days of the week are working days</p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {DAY_NAMES.map((name, index) => {
              const isWorking = defaultCal?.workingDays?.includes(index) ?? (index >= 1 && index <= 5);
              return (
                <button
                  key={index}
                  onClick={() => toggleWorkingDay(index)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                    isWorking
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
          {defaultCal && (
            <p className="text-sm text-muted-foreground mt-3">
              Calendar: {defaultCal.name}
              {defaultCal.isDefault && <Badge variant="secondary" className="ml-2">Default</Badge>}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Holidays & Exceptions</h2>
          <p className="text-sm text-muted-foreground">Manage holidays, closures, and special working days</p>
        </div>
        {exceptions.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <CalendarDays className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No exceptions configured</p>
              <p className="text-xs text-muted-foreground">Add holidays and closures to your calendar.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {exceptions.map((exc) => (
              <div key={exc.id} className="flex items-center justify-between p-3 rounded-md border">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{exc.name}</span>
                    <Badge
                      variant={exc.exceptionType === "closure" ? "destructive" : exc.exceptionType === "working_day" ? "secondary" : "default"}
                      className="text-[10px]"
                    >
                      {exc.exceptionType === "working_day" ? "Working Day" : exc.exceptionType.charAt(0).toUpperCase() + exc.exceptionType.slice(1)}
                    </Badge>
                    {exc.isRecurring && <Badge variant="outline" className="text-[10px]">Recurring</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(exc.exceptionDate).toLocaleDateString("en-US", {
                      month: "short", day: "numeric",
                      year: exc.isRecurring ? undefined : "numeric",
                    })}
                    {exc.description ? ` · ${exc.description}` : ""}
                  </p>
                </div>
                <Button
                  variant="ghost" size="icon" className="h-8 w-8"
                  onClick={() => deleteExceptionMutation.mutate(exc.id)}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" /> Date Calculator
          </CardTitle>
          <p className="text-sm text-muted-foreground">Preview working day calculations using your calendar</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-1 block">Start Date</label>
              <Input
                type="date"
                value={calcStart}
                onChange={(e) => { setCalcStart(e.target.value); setCalcResult(null); }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Working Days</label>
              <Input
                type="number"
                value={calcDays}
                onChange={(e) => { setCalcDays(e.target.value); setCalcResult(null); }}
              />
            </div>
            <Button onClick={handleCalculate}>
              <Calculator className="h-4 w-4 mr-1" /> Calculate
            </Button>
          </div>
          {calcResult && (
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <span className="text-sm text-muted-foreground">Result:</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{calcResult}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={exceptionDialog} onOpenChange={setExceptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Calendar Exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name</label>
              <Input
                value={excForm.name}
                onChange={(e) => setExcForm({ ...excForm, name: e.target.value })}
                placeholder="e.g., Independence Day"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description (optional)</label>
              <Input
                value={excForm.description}
                onChange={(e) => setExcForm({ ...excForm, description: e.target.value })}
                placeholder="e.g., Federal holiday"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <Input
                type="date"
                value={excForm.exceptionDate}
                onChange={(e) => setExcForm({ ...excForm, exceptionDate: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select
                value={excForm.exceptionType}
                onValueChange={(v) => setExcForm({ ...excForm, exceptionType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="holiday">Holiday</SelectItem>
                  <SelectItem value="closure">Closure</SelectItem>
                  <SelectItem value="working_day">Working Day (override)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={excForm.isRecurring}
                onChange={(e) => setExcForm({ ...excForm, isRecurring: e.target.checked })}
              />
              Recurring annually
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExceptionDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addExceptionMutation.mutate()}
              disabled={!excForm.name || !excForm.exceptionDate || addExceptionMutation.isPending}
            >
              {addExceptionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Exception
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
